import React, { useState, useEffect, useCallback, Suspense, useRef } from 'react';
import { useSpring, animated } from 'react-spring';
import './BoxGlasses.css';
import Anima from '../main/Anima';
import { useFetchProductData } from '../main/useFetchProductData';
import { useGlobal } from '../main/GlobalContext';
import AnimationDisplay from '../display/AnimationDisplay';

// Lazy load des composants potentiellement lourds
const Display = React.lazy(() => import('../display/Display'));
const ContentSection = React.lazy(() => import('./ContentSection'));

// Modal Component
const Modal = ({ isOpen, onClose, children, modelName }) => {
  const [display, setDisplay] = useState(isOpen);

  useEffect(() => {
    let timer;
    if (isOpen) {
      setDisplay(true);
    } else {
      timer = setTimeout(() => setDisplay(false), 500);
    }
    return () => clearTimeout(timer);
  }, [isOpen]);

  const overlayAnimation = useSpring(Anima.overlayAnimationConfig(isOpen));
  const contentAnimation = useSpring(Anima.contentAnimationConfig(isOpen));

  if (!display) {
    return null;
  }

  return (
    <animated.div className={`Overlay ${modelName}`} style={overlayAnimation} onClick={onClose}>
      <animated.div className="Modal" style={contentAnimation} onClick={(e) => e.stopPropagation()}>
        {children}
      </animated.div>
    </animated.div>
  );
};

// BoxGlasses Component
const BoxGlasses = ({ isOpen, onClose, sceneType, modelName }) => {
  // Si 'sceneType' représente la couleur sélectionnée
  const color = sceneType;

  // Inclure 'price' dans la destructuration
  const { stock, price } = useFetchProductData(modelName, color);

  const { setActiveSection } = useGlobal();

  // Références pour accéder aux meshes et à la caméra dans le composant Display
  const meshesRef = useRef([]);
  const cameraRef = useRef(null);

  // Fonction de nettoyage lors de la fermeture du modal
  const handleClose = useCallback(() => {
    setActiveSection(0);

    // Appel à AnimationDisplay.cleanup avec les meshes et la caméra pour réinitialiser l'état
    if (meshesRef.current.length > 0 && cameraRef.current) {
      AnimationDisplay.cleanup(meshesRef.current, cameraRef.current);
    }

    // Fermer le modal
    onClose();
  }, [onClose, setActiveSection]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} modelName={modelName}>
      <button onClick={handleClose} className="close-button">+</button>

      <div className="left-column">
        {/* Utilisation de Suspense pour le lazy loading de Display */}
        <Suspense fallback={<div>Chargement de la scène...</div>}>
          <Display
            sceneType={sceneType}
            modelName={modelName}
            meshesRef={meshesRef} // Transmettre les références des meshes
            cameraRef={cameraRef} // Transmettre la référence de la caméra
          />
        </Suspense>
      </div>

      <div className="right-column">
        {/* Utilisation de Suspense pour le lazy loading de ContentSection */}
        <Suspense fallback={<div>Chargement du contenu...</div>}>
          <ContentSection
            modelName={modelName}
            color={color}
            stock={stock}
            price={price} // Passer 'price' en prop
          />
        </Suspense>
      </div>
    </Modal>
  );
};

export default BoxGlasses;